import React from "react"
import { connect } from "react-redux"

import * as OutputSelector from "../redux/selectors/output-selector"
import { useEFSData } from "hooks/efs-input"
import { serializeStores } from "redux/selectors/util-selectors"
import * as mutator from "services/mutator/mutator.service"
import { toData } from "utils/string_utils"
import {
  getSelectedPlanName,
  getAllPricings,
  getAllPlanNames
} from "redux/selectors/plan-selectors"
import { getAllPlansButtonClicked } from "redux/selectors/basket-selectors"
import StringInject from "stringinject"
import { getCachedOutputAsObject } from "services/cachedRandom/random.service"
import {
  getHybridHubPrice,
  getWifiPrice,
  getAllExtras
} from "redux/selectors/extras-selector"
import { OutputLine } from "./OutputLine"

// Component to display all the outputs on the screen
const Output = ({
  excludeVAT,
  vatDivider,
  selectedPhoneID,
  selectedPlanID,
  selectedExtraIDs,
  monthlySpend,
  handsetSpend,
  airtimeSpend,
  upfrontSpend,
  extrasSpend,
  dataAmount,
  was5GPicked,
  isUnlimitedData,
  entertainmentSelected,
  serializedStore,
  selectedPhoneName,
  selectedO2Plan,
  monthsAmount,
  minutesAmount,
  textsAmount,
  networkAmount,
  extrasText,
  benefits,
  selectedPlanName,
  planData,
  wasShowAllPlansButtonClicked,
  planPrices,
  planNames,
  hybridHubCost,
  wifiCost,
  addOnList
}) => {
  const efsdata = useEFSData()

  let monthsPlan,
    dataPlan,
    minutesPlan,
    textsPlan,
    networkPlan,
    monthlyCostExport,
    upfrontCostExport

  if (!selectedPlanID && !selectedO2Plan) {
    monthsPlan = dataPlan = minutesPlan = textsPlan = networkPlan = ""
  } else {
    if (efsdata.storeName === "O2") {
      monthsPlan = selectedO2Plan.duration
      dataPlan = toData(selectedO2Plan.data.value)
      minutesPlan = "Unlimited"
      textsPlan = "Unlimited"
      networkPlan = "4G"
      monthlyCostExport = (
        selectedO2Plan.cost + selectedO2Plan.data.cost
      ).toFixed(2)
      upfrontCostExport = selectedO2Plan.upfront
    } else {
      monthsPlan = monthsAmount
      dataPlan = toData(dataAmount)
      minutesPlan = minutesAmount
      textsPlan = textsAmount
      networkPlan = networkAmount
      monthlyCostExport = (monthlySpend + extrasSpend).toFixed(2)
      upfrontCostExport = upfrontSpend
    }
  }

  const templateData = {
    currentHalo: efsdata.haveHalo === 1 ? "+ Halo 1" : " ",
    haloUpgrade: efsdata.haveHalo === 1 ? "+ Halo 3" : "+ Halo 1"
  }

  let so_planName = planData && planData.name

  if (planData && planData.postName) {
    so_planName += StringInject(planData.postName, templateData)
  }

  let stringObject = JSON.stringify({
    storeName: efsdata.storeName,
    planName: so_planName,
    downloadSpeed: planData && planData.downloadSpeed,
    downloadSpeedUnit: planData && planData.downloadSpeedUnit,
    contractLength: planData && planData.contractLength,
    lineRental: planData && planData.lineRental,
    device: selectedPhoneName ? selectedPhoneName.name : "",
    months: monthsPlan,
    data: dataPlan,
    network: networkPlan,
    minutes: minutesPlan,
    texts: textsPlan,
    extras: extrasText,
    benefits: benefits,
    monthly: monthlyCostExport,
    upfront: upfrontCostExport
  })

  // We need to add plan prices - which are going to be in separate variables...
  // So we need to create an array of mappings the varnames with the prices
  const priceVarNames = [
    efsdata.outputPrice1,
    efsdata.outputPrice2,
    efsdata.outputPrice3,
    efsdata.outputPrice4,
    efsdata.outputPrice5,
    efsdata.outputPrice6,
    efsdata.outputPrice7
  ]

  const planPriceMapping = planPrices.map((price, i) => ({
    price,
    varName: priceVarNames[i]
  }))

  const planNamesVarnames = [
    efsdata.outputPlanName1,
    efsdata.outputPlanName2,
    efsdata.outputPlanName3,
    efsdata.outputPlanName4,
    efsdata.outputPlanName5,
    efsdata.outputPlanName6,
    efsdata.outputPlanName7
  ]

  const planNamesMapping = planNames.map((name, i) => ({
    name,
    varName: planNamesVarnames[i]
  }))

  // Get the random cached output
  const randomCache = getCachedOutputAsObject()
  const instantOnShown = randomCache["display-instant-on"]

  return (
    <>
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputMutationSerialized}
        value={mutator.getRandomPicks()}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputReduxSerialized}
        value={serializedStore}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputCondition}
        value={efsdata.condition}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputSelectedPhone}
        value={selectedPhoneID}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputSelectedPlan}
        value={selectedPlanID}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputSelectedExtras}
        value={selectedExtraIDs}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputMonthlySpend}
        value={
          excludeVAT
            ? Math.round(monthlySpend * vatDivider * 100) / 100
            : monthlySpend
        }
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputHandsetSpend}
        value={
          excludeVAT
            ? Math.round(handsetSpend * vatDivider * 100) / 100
            : handsetSpend
        }
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputAirtimeSpend}
        value={
          excludeVAT
            ? Math.round(airtimeSpend * vatDivider * 100) / 100
            : airtimeSpend
        }
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputUpfrontSpend}
        value={
          excludeVAT
            ? Math.round(upfrontSpend * vatDivider * 100) / 100
            : upfrontSpend
        }
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputExtrasSpend}
        value={
          excludeVAT
            ? Math.round(extrasSpend * vatDivider * 100) / 100
            : extrasSpend
        }
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputDataAmount}
        value={dataAmount}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputIs5g}
        value={was5GPicked ? "1" : "0"}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputIsunlimited}
        value={isUnlimitedData ? "1" : "0"}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputAddedEntertainment}
        value={entertainmentSelected}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputStringObject}
        value={stringObject}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputShowButtonClick}
        value={wasShowAllPlansButtonClicked}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputPlanName}
        value={selectedPlanName}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputInstantOnShown}
        value={instantOnShown}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputWifiExtrasCost}
        value={wifiCost}
      />
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputHybridHubExtrasCost}
        value={hybridHubCost}
      />
      {planPriceMapping.map(({ price, varName }, i) => (
        <OutputLine
          key={`price-${varName}-${i}`}
          type={efsdata.showOutput}
          varname={varName}
          value={price}
        />
      ))}
      {planNamesMapping.map(({ name, varName }, i) => (
        <OutputLine
          key={`planname-${varName}-${i}`}
          type={efsdata.showOutput}
          varname={varName}
          value={name}
        />
      ))}
      <OutputLine
        type={efsdata.showOutput}
        varname={efsdata.outputAddonList}
        value={addOnList.toString()}
      />
    </>
  )
}

const mapStateToProps = state => ({
  selectedPhoneID: state.basket.phoneID,
  selectedPlanID: state.basket.planID,
  selectedExtraIDs: state.basket.extras.join(","),
  monthlySpend: OutputSelector.getTotalMonthlySpend(state),
  handsetSpend: OutputSelector.getTotalHandsetSpend(state),
  airtimeSpend: OutputSelector.getTotalAirtimeSpend(state),
  upfrontSpend: OutputSelector.getTotalUpfrontSpend(state),
  extrasSpend: OutputSelector.getTotalExtrasSpend(state),
  dataAmount: OutputSelector.getDataAmount(state),
  isUnlimitedData: OutputSelector.isUnlimitedData(state),
  was5GPicked: OutputSelector.was5GPicked(state),
  excludeVAT: state.plans.options.excludeVAT,
  vatDivider: state.plans.options.vatDivider,
  entertainmentSelected: OutputSelector.wasEntertainmentSelected(state),
  serializedStore: serializeStores(state),
  selectedPhoneName: state.devices.byID[state.basket.phoneID],
  selectedO2Plan: state.basket.builtPlan,
  monthsAmount: OutputSelector.getMonthsAmount(state),
  textsAmount: OutputSelector.getTextsAmount(state),
  minutesAmount: OutputSelector.getMinutesAmount(state),
  networkAmount: OutputSelector.getNetworkAmount(state),
  extrasText: OutputSelector.getExtrasText(
    state.basket.extras,
    state.basket.phoneID,
    state.basket.planID,
    state
  ),
  benefits: OutputSelector.getBenefitsText(state.basket.benefits, state),
  selectedPlanName: getSelectedPlanName(state),
  planData:
    state.plans.byID[state.basket.planID] &&
    state.plans.byID[state.basket.planID].data,
  wasShowAllPlansButtonClicked: getAllPlansButtonClicked(state),
  planPrices: getAllPricings(state),
  planNames: getAllPlanNames(state),
  hybridHubCost: getHybridHubPrice(state),
  wifiCost: getWifiPrice(state),
  addOnList: getAllExtras(state)
})

export default connect(mapStateToProps)(Output)
